import { breakdownRemainder } from '../finance/breakdown-remainder.js'
import type { BreakdownSummary } from '../finance/breakdown-remainder.js'
import { categoriaEfetiva, valoresDaLinha } from './receipt.js'
import type { ReceiptItem } from './receipt.js'
import type { Cents } from '../../shared/money/cents.js'
import type { CivilDate } from '../../shared/date/civil.js'
import { learnFrom } from '../product/matching.js'
import type { KnownMatch } from '../product/matching.js'
import { baseUnitOf, pricePerBaseUnit } from '../product/unit.js'
import type { BaseUnit, MilliCents } from '../product/unit.js'
import type { Product } from '../product/product.js'

/**
 * O que a confirmação de uma nota produz — a entrega "Confirmação" da
 * `Fase 8 - Nota fiscal e IA`.
 *
 * *"Gera breakdown agregado por categoria **pela mesma `breakdownRemainder`**
 * que a Fase 5 usa, e não por uma segunda conta escrita aqui: agregar itens de
 * nota é onde o teto do total é mais fácil de estourar, e é o caminho que menos
 * deve ter uma aritmética própria."*
 *
 * Este módulo obedece isso literalmente: ele **agrega**, e a validação do teto é
 * de `breakdownRemainder`, chamada aqui. Não há um `if (soma > total)` neste
 * arquivo.
 *
 * ## Função pura, e nada é gravado antes da confirmação
 *
 * *"Nada financeiro muda antes da confirmação"* é item da definição de pronto.
 * Com o cálculo puro, o que a tela mostra na revisão e o que a gravação executa
 * são **a mesma função** — e não duas contas que podem discordar entre o
 * "confirmar" e o que aparece depois.
 *
 * ## A categoria é da LINHA, e o produto só a sugere
 *
 * A primeira versão tirava a categoria de `line.product.categoryId`, e isso
 * quebrou no primeiro documento real que não era cupom de mercado: um **boleto
 * de condomínio**. "Taxa de condomínio", "Fundo de reserva" e "Consumo de água"
 * não são produtos, nunca vão ser, e mesmo assim são dinheiro que precisa cair
 * numa categoria.
 *
 * Agora `categoryId` vem na linha. Quem a preenche pode ser a herança do produto
 * (é o que a extração faz quando o matching casa) ou a pessoa, na revisão — e a
 * escolha dela **ganha**, porque é a decisão mais recente e a mais informada
 * sobre aquela nota.
 *
 * A consequência que importa: **uma linha sem produto agora conta no
 * breakdown**, desde que tenha categoria. Antes ela ia inteira para o resto, e
 * um boleto de condomínio virava um detalhamento vazio.
 *
 * ## Por que a agregação é por CATEGORIA, e não um item por linha
 *
 * Uma nota de mercado tem 30 linhas e uma transação de cartão tem um valor. Um
 * `BreakdownItem` por linha daria 30 itens onde a pergunta é "quanto disso foi
 * comida" — e a tela de detalhe viraria a nota fiscal de novo, que é justamente
 * o que o breakdown não é. As linhas continuam existindo como `ReceiptItem`; o
 * breakdown é o resumo delas por categoria.
 */

/** Uma linha já resolvida pela revisão — item de nota mais o produto escolhido. */
export type ConfirmedLine = {
  /**
   * O id da linha na nota. **Ele viaja até o `PricePointDraft`**, e é o que dá
   * ao ponto de preço um id derivado estável.
   *
   * A alternativa — procurar a linha pelo `productId` na hora de gravar —
   * escolhe a primeira quando a nota traz o mesmo produto duas vezes, e o
   * segundo ponto sobrescreve o primeiro em silêncio. Duas unidades do mesmo
   * detergente numa nota é o caso comum, não a exceção.
   */
  id: string
  rawText: string
  /**
   * O código interno da loja. Vai junto do aprendizado quando a pessoa corrige
   * o vínculo — é a chave mais estável que o texto. Ver `learnFrom`.
   */
  rawCode: string | null
  totalCents: Cents
  /**
   * O desconto que o cupom deu a esta linha, em centavos positivos. Ele entra
   * no breakdown e no ponto de preço; o `totalCents` ao lado continua sendo o
   * impresso. Ver `ReceiptItem.discountCents`.
   */
  discountCents: Cents
  quantity: number
  /**
   * A categoria desta linha. **Manda sobre a do produto** — ver o cabeçalho.
   * `null` cai no resto, que é onde o não classificado mora.
   */
  categoryId: string | null
  /**
   * `null` quando não há produto vinculado. Ele decide **catálogo** — ponto de
   * preço e aprendizado —, e não mais a categoria.
   */
  product: Product | null
  /**
   * Se a pessoa **corrigiu** o vínculo nesta revisão. Só a correção vira
   * aprendizado: aceitar o que já resolvia não ensina nada que já não se saiba.
   */
  corrected: boolean
  /**
   * A linha que a pessoa acrescentou à mão — frete, taxa, abatimento. Ver
   * `ReceiptItem.manual`.
   *
   * **É o que autoriza o valor negativo.** A linha lida entra em módulo, porque
   * negativo ali é devolução; a manual entra com o sinal que tem, porque foi
   * digitado de propósito.
   */
  manual?: boolean
}

/**
 * O abatimento manual é maior que a categoria de onde ele sai.
 *
 * Tipo próprio, como `BreakdownExceedsTotalError`, e pelo mesmo motivo: é a
 * única falha daqui que quem chama sabe **o que fazer com** — a tela diz de
 * qual categoria se trata e quanto falta. Sem ele, quem apareceria seria a
 * guarda de `breakdownRemainder` ("item precisa ter amountCents inteiro e
 * positivo"), que descreve o sintoma e não o que a pessoa fez.
 */
export class DiscountExceedsCategoryError extends Error {}

/**
 * A linha da revisão vista como a confirmação a vê.
 *
 * ## Existe para a TELA poder perguntar à mesma conta
 *
 * O resumo antes de confirmar (decisão 305) diz quantos preços vão para o
 * histórico. Se ele contasse do jeito dele, discordaria da Function no dia em
 * que uma regra mudasse — e a tela prometeria um preço que nunca foi gravado.
 * Com esta tradução, a tela chama `confirmReceipt` e lê a resposta.
 *
 * As mesmas três regras que `onReceiptConfirmed` aplica aos documentos:
 * **o corrigido vale sobre o lido** (`valoresDaLinha`), **só linha confirmada
 * leva produto** (a sugerida entra como dinheiro, sem catálogo), e a manual
 * carrega o seu sinal.
 */
export function linhaConfirmada(item: ReceiptItem, produto: Product | null): ConfirmedLine {
  const valores = valoresDaLinha(item)
  return {
    id: item.id,
    rawText: item.rawText,
    rawCode: item.rawCode,
    totalCents: valores.totalCents,
    discountCents: item.discountCents,
    quantity: valores.quantity,
    categoryId: item.categoryId,
    product: item.matchStatus === 'confirmed' ? produto : null,
    corrected: item.corrected,
    manual: item.manual,
  }
}

export type BreakdownDraft = {
  categoryId: string
  amountCents: Cents
}

export type PricePointDraft = {
  /** A linha que o gerou. Ver `ConfirmedLine.id`. */
  receiptItemId: string
  productId: string
  totalCents: Cents
  quantity: number
  pricePerBaseUnitMilliCents: MilliCents
  baseUnit: BaseUnit
  /** A data da compra. Vem da nota, e não do relógio de quem confirma. */
  observedAt: CivilDate
}

export type Confirmation = {
  /** Um por categoria, agregado. Ver o cabeçalho. */
  breakdown: BreakdownDraft[]
  /** Um por linha com produto e tamanho declarado. */
  pricePoints: PricePointDraft[]
  /** O que aprender das correções. */
  learnings: KnownMatch[]
  /**
   * O resumo que a transação vai carregar, **calculado por
   * `breakdownRemainder`**. É ele que valida o teto: se os itens passarem do
   * total, ela lança `BreakdownExceedsTotalError`, e essa é a única checagem de
   * teto do caminho inteiro.
   */
  summary: BreakdownSummary
  /**
   * O que sobrou sem categoria: linhas puladas, ou de produto sem categoria. É
   * a diferença que a tela de revisão precisa mostrar explícita — *"a diferença
   * entre a soma dos itens e o total da nota aparece explícita"*.
   */
  unassignedCents: Cents
}

/**
 * Monta tudo o que a confirmação vai gravar.
 *
 * `transactionAmountCents` é o valor da **transação**, não o total da nota: são
 * números diferentes e a diferença importa. A nota pode ter sido paga em duas
 * formas, ou trazer um item que virou devolução — e o breakdown pertence à
 * transação, então é o teto dela que manda.
 *
 * **Lança quando os itens passam do total.** É `breakdownRemainder` quem lança,
 * e quem chama traduz para "a soma dos itens passa do valor da compra".
 */
export function confirmReceipt(
  lines: ConfirmedLine[],
  transactionAmountCents: Cents,
  /**
   * A data da compra, lida da nota. **`null` quando a leitura não a achou, e aí
   * não há ponto de preço nenhum.**
   *
   * Um ponto de preço sem data não entra em série nenhuma: o histórico existe
   * para responder "quanto custava em agosto", e um ponto sem quando não
   * responde isso. Pior, ele é ilegível na volta — `requireCivilDate` recusa, e
   * como a tela de Produtos lê o histórico de todos os produtos de uma vez, um
   * ponto podre apagava o catálogo inteiro. Foi o que aconteceu em 2026-09-28,
   * com a Function passando `''` no lugar da data que não existia.
   *
   * O breakdown e o aprendizado continuam: a nota sem data ainda diz em que
   * categoria o dinheiro caiu, e ainda ensina o vínculo que a pessoa corrigiu.
   */
  observedAt: CivilDate | null,
  /** A loja da nota, pelo CNPJ e pelo nome. Sem ela o `rawCode` não identifica
   *  nada, e o aprendizado fica só pelo texto. */
  loja: { cnpj: string | null; name: string | null } = { cnpj: null, name: null },
  /** O piso: a categoria do documento inteiro. Ver `Receipt.categoryId`. */
  categoriaDaNota: string | null = null,
): Confirmation {
  const porCategoria = new Map<string, number>()
  const pricePoints: PricePointDraft[] = []
  const learnings: KnownMatch[] = []
  let semCategoria = 0

  for (const line of lines) {
    // Valor negativo numa linha LIDA é devolução, e ela não é um item de gasto:
    // o breakdown soma partes de uma compra, e uma parte negativa faria o resto
    // **crescer** acima do total.
    //
    // **A manual escapa do módulo**, e é a única que escapa. Ela não foi lida
    // de papel nenhum: alguém digitou o sinal de propósito para dizer "isto
    // abate". Aplicar `Math.abs` nela somaria o cupom de desconto ao gasto —
    // exatamente o contrário do que a pessoa pediu, e sem nada reclamar.
    //
    // **Menos o desconto da linha**, e é isso que faz a soma fechar com o
    // `VALOR A PAGAR` do cupom em vez do `VALOR TOTAL`. Sem isto o breakdown
    // estouraria o teto da transação por exatamente o desconto, e o histórico
    // guardaria o preço de etiqueta de uma coisa que saiu mais barata.
    const impresso = line.manual === true ? line.totalCents : Math.abs(line.totalCents)
    const valor = impresso - line.discountCents

    // Linha → produto → nota, e quem decide é `categoriaEfetiva`: a mesma
    // função que a tela usa, porque duas cópias divergiriam sem ninguém ver.
    const categoria = categoriaEfetiva(
      // `matchStatus` não viaja até aqui: quem chega com `product` preenchido
      // JÁ é linha confirmada — a Function só o preenche nesse caso.
      { categoryId: line.categoryId, matchStatus: line.product === null ? 'pending' : 'confirmed' },
      line.product,
      categoriaDaNota,
    )
    if (categoria === null) {
      // Sem categoria nenhuma, o dinheiro vai para o resto — e a tela mostra
      // isso. Inventar uma categoria seria pior do que dizer "não sei".
      semCategoria += valor
    } else {
      porCategoria.set(categoria, (porCategoria.get(categoria) ?? 0) + valor)
    }

    // Daqui para baixo é **catálogo**, e catálogo exige produto.
    if (line.product === null) continue

    if (line.corrected) {
      const aprendizado = learnFrom(
        {
          rawText: line.rawText,
          barcode: null,
          rawCode: line.rawCode,
          merchantCnpj: loja.cnpj,
          merchantName: loja.name,
        },
        line.product.id,
      )
      if (aprendizado !== null) learnings.push(aprendizado)
    }

    // Ponto de preço só com tamanho declarado — sem ele não há unidade base, e
    // um ponto sem normalização não entra em comparação nenhuma. Mesma recusa
    // do `recordPrice`. E só com DATA, pelo motivo no parâmetro.
    if (
      observedAt !== null &&
      line.product.size !== null &&
      line.product.unit !== null &&
      line.quantity > 0
    ) {
      const unitario = Math.round(valor / line.quantity)
      pricePoints.push({
        receiptItemId: line.id,
        productId: line.product.id,
        totalCents: valor,
        quantity: line.quantity,
        pricePerBaseUnitMilliCents: pricePerBaseUnit(
          unitario,
          line.product.size,
          line.product.unit,
          line.product.packQuantity,
        ),
        baseUnit: baseUnitOf(line.product.unit),
        observedAt,
      })
    }

  }

  for (const [categoryId, amountCents] of porCategoria) {
    if (amountCents < 0) {
      throw new DiscountExceedsCategoryError(
        `breakdown: o abatimento passa do que a categoria ${categoryId} tem — ela fecharia em ${String(amountCents)} centavos`,
      )
    }
  }

  const breakdown: BreakdownDraft[] = [...porCategoria.entries()]
    // Categoria que fecha em ZERO não é gasto, e `breakdownRemainder` recusa
    // item de valor zero. Um frete inteiramente abatido não deixa rastro — e
    // não derruba a confirmação inteira por causa de uma linha que se anulou.
    .filter(([, amountCents]) => amountCents !== 0)
    .map(([categoryId, amountCents]) => ({ categoryId, amountCents }))
    // Ordem estável: a maior primeiro, e o id desempata. Sem isto, duas
    // execuções sobre os mesmos dados produziriam listas em ordens diferentes,
    // e a comparação de "já está gravado" nunca fecharia.
    .sort((a, b) => b.amountCents - a.amountCents || a.categoryId.localeCompare(b.categoryId))

  return {
    breakdown,
    pricePoints,
    learnings,
    // **A única validação de teto do caminho.** Ver o cabeçalho.
    summary: breakdownRemainder(transactionAmountCents, breakdown),
    unassignedCents: semCategoria,
  }
}
